import { Callback1, lazy } from '@deqo/foundation';
import { hash } from '../../../CodesThatsGottaGoSomewhereElse';
import { PropertyHistory } from './PropertyHistory';

export type PropertyFacadeGetOpts<T> = {
    ifVarying?: T,
    useFirstIfVarying?: boolean,
}

export type HasId = { id: string };

export class PropertyFacade<T extends HasId> {

    constructor(private history: PropertyHistory, private objects: T[]) {
    }

    public get count() {
        return this.objects.length;
    }

    @lazy()
    public get key() {
        return hash('sha256', this.objects.map(x => x.id).join(''));
    }

    public keyFor(...prop: (keyof T)[]) {
        return `set:${prop.join('&')}:${this.key}`;
    }

    public check(predicate: (Callback1<boolean, T>)): boolean {
        for (const o of this.objects) {
            if (!predicate(o)) return false;
        }
        return true;
    }

    public first() {
        return this.objects[0];
    }

    public map<U>(predicate: Callback1<U, T>): U[] {
        return this.objects.map(predicate);
    }

    public get<K extends keyof T>(prop: K, opts?: PropertyFacadeGetOpts<T[K]>): T[K] | null {
        let v = null as T[K] | null;
        for (const o of this.objects) {
            const n = o[prop];
            v = v ?? n;
            if (n !== v) {
                if (opts?.useFirstIfVarying) return v;
                return opts?.ifVarying ?? null;
            }
        }
        return v;
    }

    public set<K extends keyof T>(prop: K, value: T[K] | Callback1<T[K], T[K]>): void {
        const av = value as any;
        const rv = typeof (av) === 'function' ? av(this.get(prop)) : av;
        for (const o of this.objects) {
            o[prop] = rv;
        }
    }

    public readapt<U extends HasId>(readapter: Callback1<U, T>) {
        return new PropertyFacade<U>(
            this.history,
            this.objects.map(x => readapter(x))
        );
    }
}
